import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { authAtom, AuthState } from '../store/auth';
import { parseOAuthCallback, exchangeCodeForTokens } from '../services/cognitoAuth';

/**
 * OAuth Callback Component
 *
 * Handles the OAuth callback from Cognito Hosted UI.
 * Extracts the authorization code from the URL, exchanges it for tokens,
 * and updates the authentication state.
 */
const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [, setAuth] = useAtom(authAtom);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Parse callback URL
        const { code, error: oauthError } = parseOAuthCallback();

        if (oauthError) {
          setError(oauthError);
          setIsProcessing(false);
          return;
        }

        if (!code) {
          // No code means this might be a post-logout redirect
          // Check if we have no tokens - if so, redirect to login instead of showing error
          const hasTokens = localStorage.getItem('id_token') || localStorage.getItem('access_token');

          if (!hasTokens) {
            console.log('No auth code and no tokens - likely post-logout, redirecting to root');
            // Clear any stale data and redirect to root (which will redirect to login)
            localStorage.clear();
            window.location.replace('/');
            return;
          }

          setError('No authorization code received');
          setIsProcessing(false);
          return;
        }

        // Exchange code for tokens
        const { user, accessToken, idToken } = await exchangeCodeForTokens(code);

        // Update auth state
        const newAuthState: AuthState = {
          isAuthenticated: true,
          username: user.username,
          email: user.email,
          userId: user.userId,
          groups: user.groups,
          isAdmin: user.isAdmin,
          accessToken,
          idToken,
        };

        setAuth(newAuthState);

        // Save to localStorage
        localStorage.setItem('talrev_auth', JSON.stringify(newAuthState));

        // Redirect to dashboard
        navigate('/');
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, setAuth]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Authentication Error</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Completing sign in...</h2>
          <p className="text-gray-600">Please wait while we log you in.</p>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
